import RawDataModal from './RawDataModal';
import { OpsLoading, OpsError, OpsEmpty } from './OpsStates';
import { useOpsContext } from './useOpsContext';
import { useOpsEvidenceQuery } from '../../services/useOpsBriefingService';

/**
 * 근거 보기 팝업 — 서버에서 받아 온다.
 *
 * ─────────────────────────────────────────────────────────────
 * 예전에는 mockRawData[itemId] 를 직접 참조했습니다
 * ─────────────────────────────────────────────────────────────
 * 실서버 itemId 는 'pace-{memberId}' 형식이고 목 키는 'bi_1' 입니다.
 * 그래서 보통은 팝업이 열리지 않았고 — "왜 이렇게 판단했는지 보기"를 눌러도
 * 아무 일이 안 일어났습니다 — 키가 우연히 겹치면 **다른 회원의 근거**가 떴습니다.
 * 근거 화면은 이 기능이 신뢰를 얻는 유일한 통로라서, 둘 다 최악입니다.
 *
 * 순번 근거는 프론트가 붙입니다 (adapters.toEvidenceView).
 * 훅 안에서 이미 처리되므로 여기서는 그리기만 합니다.
 */

/** 팝업 자리에 상태를 보여주는 껍데기 — 빈 화면 대신 무슨 일인지 말한다 */
const Shell = ({ onClose, children }: { onClose: () => void; children: React.ReactNode }) => (
    <div
        className="fixed inset-0 flex items-center justify-center"
        /*
          근거 팝업은 **상세 드로어 위**에 떠야 한다 (드로어 2300).
          이제 브리핑 상세가 드로어 안에서 열리고, 그 안의 "왜 이렇게 판단했는지 보기"가
          이 팝업을 부른다 — 뒤에 깔리면 눌러도 아무 일이 안 일어난 것처럼 보인다.
          지시 확인 창(2500)보다는 아래여야 한다.
        */
        style={{ background: 'rgba(12,22,40,0.6)', zIndex: 2400, padding: 24 }}
        onClick={onClose}
    >
        <div
            className="flex flex-col overflow-hidden rounded-[18px] bg-white"
            style={{ width: 520, maxHeight: '92vh', boxShadow: '0 28px 72px rgba(10,20,40,0.36)', padding: 24 }}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
            <button
                type="button"
                onClick={onClose}
                className="rounded-[10px]"
                style={{
                    marginTop: 16,
                    height: 44,
                    background: '#F1F4F9',
                    border: 'none',
                    color: '#425063',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                닫기
            </button>
        </div>
    </div>
);

type Props = {
    itemId: string;
    /** 지금 보고 있는 브리핑 날짜 (KST). 근거가 다른 날짜 것이면 밝히려고 쓴다 */
    briefingDate?: string;
    onClose: () => void;
};

const EvidenceModal = ({ itemId, briefingDate, onClose }: Props) => {
    const ctx = useOpsContext();
    const { data, isLoading, isError, refetch } = useOpsEvidenceQuery(ctx, itemId);

    if (isLoading) {
        return (
            <Shell onClose={onClose}>
                <OpsLoading variant="panel" />
            </Shell>
        );
    }

    if (isError) {
        return (
            <Shell onClose={onClose}>
                <OpsError title="근거를 불러오지 못했습니다" onRetry={() => refetch()} />
            </Shell>
        );
    }

    /*
     * 서버는 근거가 없는 항목에 200 + null 을 줍니다 (에러가 아닙니다).
     * 없는 근거를 지어내지 않고, 없다고 말합니다.
     */
    if (!data) {
        return (
            <Shell onClose={onClose}>
                <OpsEmpty
                    icon="🔍"
                    title="이 항목의 원천 데이터가 아직 없습니다"
                    body="판단에 쓴 기록을 다시 꺼낼 수 없는 항목입니다. 회원 현황 탭에서 수업 기록을 직접 확인하실 수 있습니다."
                />
            </Shell>
        );
    }

    /*
     * itemId 는 하루 안에서만 고유하다. 같은 회원이 여러 날 올라오면 같은 itemId 가 여러 날에 있고,
     * 서버는 그중 **가장 최근 날짜**의 항목을 돌려준다 (opsEvidence.service.findItem).
     * 지난 브리핑을 열어봤는데 오늘 근거가 뜨면, 원장님은 그날의 판단으로 읽는다.
     * 서버가 briefingDate 를 함께 주므로 다를 때는 밝힌다.
     *
     * ⚠️ 이건 **완화지 해결이 아니다.** 근거 API 가 date 를 받지 않아 지난 날짜의 근거는
     * 아직 가져올 수 없다. `?date=` 지원을 요청해 뒀다 —
     * BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md §8.
     */
    const otherDay =
        briefingDate && data.briefingDate && data.briefingDate !== briefingDate
            ? data.briefingDate
            : null;

    return (
        <RawDataModal
            data={data}
            notice={
                otherDay
                    ? `이 근거는 ${otherDay} 브리핑 기준입니다. ${briefingDate} 당시의 수치와 다를 수 있습니다.`
                    : undefined
            }
            onClose={onClose}
        />
    );
};

export default EvidenceModal;
